import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { postLogout } from '@/lib/logout';
import { landAfterSignIn } from '@/lib/landAfterSignIn';
import { resetPasskeyAutofill } from '@/lib/passkeys';
import { ApiError, apiRequest, markSigningOut, queryClient } from '@/lib/queryClient';
import { describeApiError, isUnreachable } from '@/lib/apiError';
import { clearRecentProjects } from '@/lib/recentProjects';
import { forgetProjectsSnapshot, setProjectsSnapshotOwner } from '@/lib/projectsSnapshot';
import { forgetCommandRecents } from '@/lib/commandRecents';
import { forgetCompanySnapshot, setCompanySnapshotOwner } from '@/lib/companySnapshot';
import { SESSION_IDLE_CODE, SESSION_ABSOLUTE_CODE } from '@shared/sessionPolicy';
import { resetSessionActivity } from '@/lib/sessionActivity';
import { clearBadgeForSignOut } from '@/lib/appBadge';
import {
  SIGNED_OUT_ELSEWHERE_KEY,
  broadcastSignedOutElsewhere,
  consumeReturnTo,
  recordSessionEnd,
  rememberReturnTo,
  setKnownSignedIn,
  type SessionEndReason,
} from '@/lib/sessionEnd';
import { appSignInFinishPath, takeAppSignIn } from '@/lib/appSignIn';
import { clearSessionEndedNotice } from '@/components/SessionEndedCard';
import { SessionUnreachable } from '@/components/SessionUnreachable';
import { LanguageProvider } from '@/components/LanguageProvider';
import { isPublicPagePath } from '@/lib/publicPages';
import { isPurchaseHost } from '@shared/purchaseHost';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  themeColor?: string;
  userGroupId?: string;
  userGroupDescription?: string;
  menuPermissions?: string[];
  companyId?: string;
  phone?: string;
  country?: string;
  profileImageUrl?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  isCompanyAdmin?: boolean;
  canDeleteContacts?: boolean;
  /** May put a task in a colleague's queue (shared/permissions.ts). */
  canAssignTasksToOthers?: boolean;
  /** May change prices — Availability › Pricing (shared/permissions.ts canEditPricing). */
  canEditPricing?: boolean;
  /** May give a discount or an incentive, or sell below list (shared/permissions.ts canGiveDiscounts). */
  canGiveDiscounts?: boolean;
  hasPasskeys?: boolean;
  passkeyPromptDismissedAt?: string | null;
}

interface TwoFactorStatus {
  required: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  /** A passkey could satisfy the second factor. */
  passkeyAvailable?: boolean;
  /** An authenticator app is set up. */
  totpEnabled?: boolean;
  recoveryCodesRemaining?: number;
  /** The masked address the emailed code goes to. */
  emailHint?: string | null;
}

/**
 * Why the last session check did not produce an answer. Null when it did.
 *
 * "Could not ASK" is a different fact from "not signed in", and it gets a
 * different screen (components/SessionUnreachable.tsx). Treating the first as
 * the second is what sent signed-in people to the login page on a bad
 * connection — and each re-login then spent one of the office IP's
 * rate-limited attempts.
 */
export interface SessionUnreachableState {
  message: string;
  /** A session was confirmed earlier in this tab. */
  wasSignedIn: boolean;
  /** When the next automatic attempt fires (ms epoch). */
  nextAttemptAt: number;
  retrying: boolean;
}

interface AuthContextType {
  user: User | null;
  twoFactorStatus: TwoFactorStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logoutInProgress: boolean;
  /** Set while the server cannot be reached. `isLoading` stays true meanwhile. */
  unreachable: SessionUnreachableState | null;
  checkSession: () => Promise<void>;
  /**
   * `reason` marks an INVOLUNTARY sign-out (idle timeout, expired session,
   * signed out in another tab) so the login page can say what happened, and
   * remembers where the user was so signing back in returns there. Omit it for
   * the Sign Out menu item: explaining a thing the user just chose to do reads
   * as an error.
   */
  logout: (options?: { reason?: SessionEndReason; skipServer?: boolean }) => void;
  refetchTwoFactorStatus: () => Promise<void>;
  setTwoFactorVerified: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Back off between automatic attempts while unreachable: 2s, 4s, 8s … 30s. */
export function unreachableBackoffMs(attempt: number): number {
  return Math.min(2000 * 2 ** attempt, 30_000);
}

/** A failure that says nothing about the session: nothing answered, or the server could not. */
function couldNotAsk(err: unknown): boolean {
  if (isUnreachable(err)) return true;
  return err instanceof ApiError && (err.status >= 500 || err.status === 429 || err.status === 408);
}

const AUTH_SCREENS = ['/login', '/forgot-password', '/reset-password', '/verify'];

/**
 * A page a BUYER reaches — a quote, the registration form, the public roadmap,
 * the legal pages. Nobody there is signed in or expects to be, so a session
 * check that cannot reach the server must not put "you haven't been signed
 * out" in front of them; the page renders and its own requests report the
 * outage in its own terms.
 */
function isPublicContentLocation(): boolean {
  return isPublicPagePath(window.location.pathname);
}

/** A protected page someone was bounced off — worth coming back to. */
function isProtectedLocation(): boolean {
  const p = window.location.pathname;
  if (p === '/' || AUTH_SCREENS.includes(p)) return false;
  // A buyer's link (/sign, /id, /confirm-email, /review, /worksheet-response,
  // /documents) is not somewhere a rep was bounced off (lib/publicPages.ts).
  return !isPublicPagePath(p);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Whose device copy of the project list may be drawn (lib/projectsSnapshot.ts).
  // During render, so it is set before any child that reads it renders with
  // this user; idempotent, and null whenever nobody is signed in.
  setProjectsSnapshotOwner(user?.id ?? null);
  // And of the company Company Settings shows (lib/companySnapshot.ts).
  setCompanySnapshotOwner(user?.id ?? null);
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logoutInProgress, setLogoutInProgress] = useState(false);
  const [unreachable, setUnreachable] = useState<SessionUnreachableState | null>(null);

  // CRITICAL: Use abort controller to prevent race conditions from concurrent checkSession calls
  const abortControllerRef = useRef<AbortController | null>(null);
  /** Consecutive unreachable attempts, for the backoff. */
  const unreachableAttemptRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const userRef = useRef<User | null>(null);
  userRef.current = user;
  /** A session has been confirmed in this tab since it loaded. */
  const everSignedInRef = useRef(false);

  const clearRetryTimer = () => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const checkSession = async () => {
    // CRITICAL: Abort any in-flight checkSession request to prevent race conditions
    abortControllerRef.current?.abort();
    clearRetryTimer();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    // CRITICAL: Keep isLoading true until BOTH user AND 2FA status are resolved
    setIsLoading(true);
    setUnreachable((u) => (u ? { ...u, retrying: true } : u));

    /** Could not ask: keep everything as it is, say so, and try again later. */
    const becomeUnreachable = (err: unknown) => {
      const attempt = unreachableAttemptRef.current;
      unreachableAttemptRef.current = attempt + 1;
      const delay = unreachableBackoffMs(attempt);
      console.warn('[AuthContext] Session check could not reach the server:', err);
      setUnreachable({
        message: describeApiError(err),
        wasSignedIn: everSignedInRef.current,
        nextAttemptAt: Date.now() + delay,
        retrying: false,
      });
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        void checkSessionRef.current();
      }, delay);
    };

    /** Not signed in — an answer, and possibly the end of a session this tab had. */
    const becomeSignedOut = (err: ApiError | null) => {
      // Distinguish "was never signed in" from "was signed in until the policy
      // ended it". Both land on /login, but only the second is owed an
      // explanation — this is the path taken when the tab was closed and the
      // app is opened again later, with no in-app timer running to catch it.
      if (err?.code === SESSION_IDLE_CODE) recordSessionEnd('idle');
      else if (err?.code === SESSION_ABSOLUTE_CODE) recordSessionEnd('expired');
      // A deep link, a notification tap or a bookmark that lands on a lapsed
      // session: remember it, so signing in goes there rather than to /home.
      if (isProtectedLocation()) rememberReturnTo();
      setKnownSignedIn(false);
      setUser(null);
      setTwoFactorStatus(null);
      // logoutInProgress stays as it is: it is only cleared by a new sign-in.
    };

    try {
      // purchase.salefish.app holds no Console session — a cookie is per host
      // (shared/purchaseHost.ts) — so there is nothing to ask: everyone there
      // is a buyer or partner and signed out by definition. Asking only put
      // two 401s in front of every buyer page (2026-10-31).
      if (isPurchaseHost(window.location.hostname)) return becomeSignedOut(null);

      /* Both questions at once. They used to be asked in series — the user,
         then the 2FA status — which put two round trips in front of every
         cold launch. Asked together and judged afterwards, exactly as before:
         the user's answer decides signed-in / signed-out / unreachable first,
         and the 2FA answer is only read for a session that exists. A
         signed-out launch pays one extra 401, in parallel. */
      const [userResult, twoFaResult] = await Promise.allSettled([
        apiRequest('GET', '/api/auth/user', undefined, { signal, authOptional: true })
          .then((res) => res.json() as Promise<User>),
        apiRequest('GET', '/api/auth/2fa/status', undefined, { signal, authOptional: true })
          .then((res) => res.json() as Promise<TwoFactorStatus>),
      ]);
      if (signal.aborted) return;

      if (userResult.status === 'rejected') {
        const err = userResult.reason;
        if (couldNotAsk(err) && !isPublicContentLocation()) return becomeUnreachable(err);
        return becomeSignedOut(err instanceof ApiError && !couldNotAsk(err) ? err : null);
      }
      const userData = userResult.value;

      // Don't log the address itself — this ends up in client-side error and
      // session-recording tools, and in support screen-shares.
      if (import.meta.env.DEV) console.log('[AuthContext] User authenticated:', userData.email);

      // CRITICAL: the 2FA status is settled BEFORE clearing isLoading.
      let twoFa: TwoFactorStatus;
      if (twoFaResult.status === 'fulfilled') {
        twoFa = twoFaResult.value;
      } else {
        const err = twoFaResult.reason;
        if (couldNotAsk(err)) return becomeUnreachable(err);
        if (err instanceof ApiError && err.status === 401) return becomeSignedOut(err);
        // SECURITY: Fail closed — any other refusal requires 2FA. (The server
        // enforces it regardless; this only decides which screen to draw.)
        console.error('[AuthContext] 2FA status refused, requiring 2FA:', err);
        twoFa = {
          required: true,
          emailVerified: userData.emailVerified || false,
          phoneVerified: userData.phoneVerified || false,
        };
      }
      if (signal.aborted) return;

      unreachableAttemptRef.current = 0;
      setUnreachable(null);
      everSignedInRef.current = true;
      setKnownSignedIn(true);
      setUser(userData);
      // CRITICAL: Clear logoutInProgress when we have a new authenticated session
      setLogoutInProgress(false);
      setTwoFactorStatus(twoFa);

      if (twoFa.required) {
        // The /verify page (or the sign-in card) will take them through it;
        // keep where they were so verifying returns them there.
        if (isProtectedLocation()) rememberReturnTo();
      } else {
        // Signed in: the "why were you signed out" card has done its job.
        clearSessionEndedNotice();
        // Signing in for another app ("Sign in with SaleFish", lib/appSignIn.ts):
        // hand back to it. Before the return-to below, which is Console's own.
        const appSignIn = takeAppSignIn();
        if (appSignIn) {
          landAfterSignIn(appSignInFinishPath(appSignIn));
          return;
        }
        // …and back to whatever they were opening when the session ran out.
        // Spent HERE because this is the one place every door passes through:
        // the password form, a passkey, the 2FA screen, and the SSO round trip,
        // which comes back from the server to "/" with no screen of ours in
        // between. `null` on an ordinary reload deep inside the app.
        const back = consumeReturnTo();
        // A real page load, not a router push — see lib/landAfterSignIn.ts.
        if (back) landAfterSignIn(back);
      }
    } finally {
      // Clear isLoading unless a NEWER checkSession has taken over — that one
      // owns the flag and will clear it when it settles. While unreachable it
      // stays set: the app must not render as signed-in or signed-out on a
      // guess, and SessionUnreachable is drawn over the loader instead.
      //
      // This used to test `abortController.signal.aborted`, which conflates
      // "superseded by a newer call" with "aborted with no successor" — logout()
      // aborts without starting a successor, and that path left isLoading stuck
      // true forever. Comparing against the ref distinguishes the two.
      const supersededByNewerCall = abortControllerRef.current !== abortController;
      if (!supersededByNewerCall) {
        abortControllerRef.current = null;
        if (retryTimerRef.current === null) setIsLoading(false);
      }
    }
  };

  // Timers and listeners call the latest checkSession, not the one they closed over.
  const checkSessionRef = useRef(checkSession);
  checkSessionRef.current = checkSession;

  const retryNow = useCallback(() => {
    clearRetryTimer();
    void checkSessionRef.current();
  }, []);

  // Come back the moment the connection does.
  useEffect(() => {
    if (!unreachable) return;
    const onBack = () => retryNow();
    const onVisible = () => {
      if (document.visibilityState === 'visible') retryNow();
    };
    window.addEventListener('online', onBack);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onBack);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [unreachable, retryNow]);

  useEffect(() => () => clearRetryTimer(), []);

  const refetchTwoFactorStatus = async () => {
    if (!user) return;
    try {
      const res = await apiRequest('GET', '/api/auth/2fa/status', undefined, { authOptional: true });
      setTwoFactorStatus(await res.json());
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const logout = async (options?: { reason?: SessionEndReason; skipServer?: boolean }) => {
    // CRITICAL: Abort any in-flight checkSession requests to prevent race conditions
    abortControllerRef.current?.abort();
    clearRetryTimer();
    setUnreachable(null);

    // Before anything can race it: a 401 from a request still in flight must not
    // read as a session that ended under the user.
    setKnownSignedIn(false);

    // …and must not navigate away first. This sign-out owns the trip to the
    // sign-in page and the note that explains it; a 401 arriving during the
    // server call below used to beat it to /login with no note at all, so an
    // idle sign-out landed on a sign-in page that said nothing (QA 2026-10-28).
    markSigningOut();
    // The note goes down now as well as after the clear below: if the page is
    // left before this function finishes, the reason is already there.
    if (options?.reason) recordSessionEnd(options.reason);

    // CRITICAL: Set logoutInProgress FIRST to block any auth redirects
    setLogoutInProgress(true);

    // Where they were — only worth keeping for a sign-out they did not choose.
    const here = window.location.pathname + window.location.search;
    const involuntary = Boolean(options?.reason);

    window.gtag?.('event', 'logout');
    if (!options?.skipServer) {
      try {
        // Must go through apiRequest so the CSRF token is attached — POST
        // /api/auth/logout is CSRF-protected, and a tokenless request is
        // rejected 403, leaving the server session alive.
        await postLogout();
      } catch (error) {
        console.error('[AuthContext] Logout API call failed:', error);
        // Continue with logout even if API call fails
      }
    }

    // CRITICAL: Clear session storage
    sessionStorage.clear();

    // AFTER the clear, not before — both notes are read by the login page, and
    // clearing would eat them.
    if (options?.reason) {
      recordSessionEnd(options.reason);
      if (involuntary && isProtectedLocation()) rememberReturnTo(here);
    } else {
      // Pressed Sign Out: every other tab in this browser shares the cookie that
      // was just destroyed. Tell them, so they explain it rather than failing on
      // their next request.
      broadcastSignedOutElsewhere();
    }

    // The Home Screen badge is cleared on a DELIBERATE sign-out only — that is
    // somebody saying they are done. A timeout leaves it: the unread
    // notifications behind it are no less real because the session lapsed.
    if (!options?.reason) void clearBadgeForSignOut();

    // Idle clocks are keyed to nobody in particular; leaving them set would
    // hand the next person to sign in on this browser the previous user's
    // timestamps.
    resetSessionActivity();

    // The login page's conditional-UI passkey ceremony is module state holding a
    // challenge issued into the session this sign-out just destroyed.
    resetPasskeyAutofill();

    // CRITICAL: Drop every cached API response. `queryClient` is a module-level
    // singleton, and until the page load at the end of this function lands the
    // tree re-renders signed-out against it — nothing of the previous user's may
    // be read from memory in that window (a shared sales-office workstation).
    queryClient.cancelQueries();
    queryClient.clear();

    // Reset language to browser default so the login page shows the system
    // language for the next person using this browser.
    localStorage.removeItem('salefish-language');

    // Per-user UI state keyed to the outgoing account.
    const previousUserId = user?.id;
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('selectedProjectName');
    localStorage.removeItem('lastContext');
    // The company colours theme-init.js paints before first paint — the next
    // person at this device must not start in this account's brand.
    // Same key as COMPANY_THEME_CACHE_KEY in contexts/CompanyThemeProvider.
    localStorage.removeItem('salefish:company-theme');
    // The device's copy of this account's project list (and the image paths
    // the launch warm-up reads from it) — lib/projectsSnapshot.ts.
    forgetProjectsSnapshot();
    // And of the company Company Settings showed — lib/companySnapshot.ts.
    forgetCompanySnapshot();
    // What this account last opened from the command bar — lib/commandRecents.ts.
    forgetCommandRecents();
    if (previousUserId) {
      clearRecentProjects(previousUserId);
      // recentlyViewedContacts is keyed per user AND per project, so sweep by prefix.
      const prefix = `recentlyViewedContacts:${previousUserId}:`;
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(prefix)) localStorage.removeItem(key);
      }
    }

    // CRITICAL: Clear auth state to prevent stale state from triggering redirects
    setUser(null);
    setTwoFactorStatus(null);
    setIsLoading(false);

    // A full navigation, not SPA routing (2026-09-29), for two reasons. The
    // iOS 26 top edge: WebKit freezes the colour it sampled at the reader's
    // first tap and drops it when that element unmounts, so leaving the shell
    // client-side blurred the sign-in page — a page load samples it fresh
    // (lib/landAfterSignIn.ts has the whole story). And the sign-in page is
    // then a fresh document: no Radix dropdown left holding `document.body`'s
    // pointer-events over the login form, which the SPA transition needed a
    // requestAnimationFrame release for. Everything above still runs first —
    // the notes and the remembered destination are what that page reads.
    // `assign`, not `replace`: Back from the sign-in page just meets the auth
    // guard again.
    window.location.assign('/login');
  };

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // Another tab pressed Sign Out. The cookie is shared, so this tab's session is
  // already gone — follow it out now, with the reason, instead of failing on the
  // next request with nothing to say.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SIGNED_OUT_ELSEWHERE_KEY || !event.newValue) return;
      if (!userRef.current) return;
      void logoutRef.current({ reason: 'elsewhere', skipServer: true });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Manually mark 2FA as verified (avoids session cache race condition)
  const setTwoFactorVerified = () => {
    setTwoFactorStatus((prev) => ({ ...prev!, required: false }));
  };

  // Update user data optimistically (for instant UI updates)
  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  // Check session on mount
  useEffect(() => {
    void checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    twoFactorStatus,
    isLoading,
    isAuthenticated: !!user,
    logoutInProgress,
    unreachable,
    checkSession,
    logout,
    refetchTwoFactorStatus,
    setTwoFactorVerified,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Drawn over the app's loader while the session cannot be checked — here
          rather than in AppContent so no route has to remember it. */}
      {/* AuthProvider sits OUTSIDE the app's LanguageProvider (App.tsx), so
          this screen gets its own, reading the same stored choice — otherwise
          it would always be drawn in English. */}
      {unreachable && (
        <LanguageProvider storageKey="salefish-language">
          <SessionUnreachable state={unreachable} onRetry={retryNow} />
        </LanguageProvider>
      )}
    </AuthContext.Provider>
  );
}

/**
 * The signed-in account where there is one, or null — for a component that
 * may render outside the AuthProvider: the route fallbacks
 * (ui/route-skeleton.tsx), which their tests draw on their own and which
 * only ever use it to draw the same rows the page will.
 */
export function useOptionalAuth() {
  return useContext(AuthContext) ?? null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
